import type { AlarmeCatalogo, AlarmeGeradora, EventoAlarme } from '../../types';

/**
 * Agrupamento e tradução dos alarmes de geradora.
 *
 * CONTEXTO (31/08/2026). A tela de alarmes mostrava uma parede de cards
 * idênticos e o time leu como "alarmes duplicados". Não eram: cada linha era um
 * inversor DIFERENTE. Um evento de rede derruba os N inversores da usina no
 * mesmo segundo e a fonte devolve uma linha por inversor — em 28/08, das 14:36
 * às 14:38, cinco usinas dispararam juntas.
 *
 * O dado por inversor é correto e fica no banco. Quem agrupa é a tela, aqui.
 */

/** Janela para considerar que duas linhas são o mesmo evento.
 *
 *  15 min cobre os dois padrões observados: a Chint dispara os inversores no
 *  mesmo segundo, e a SAJ repete o mesmo alarme com 10 s de diferença e id
 *  próprio (que o gatilho do banco não agrupa, justamente por ter id). Acima
 *  disso é reincidência, e reincidência merece card próprio. */
export const JANELA_EVENTO_MS = 15 * 60 * 1000;

/** Curinga do catálogo: vale para qualquer variante do código. */
const CURINGA = '*';

const chave = (fonte: string, code: string, info: string) => `${fonte}|${code}|${info}`;

/**
 * Índice do catálogo para busca por (fonte, código, variante).
 *
 * A busca tenta a variante EXATA e só então o curinga. É isso que impede o
 * "Out of phase" de aparecer com o texto de "Tensão da rede fora do limite":
 * os dois vivem sob o mesmo `error_code` na FOMware.
 */
export function indexarCatalogo(itens: AlarmeCatalogo[]): Map<string, AlarmeCatalogo> {
    const m = new Map<string, AlarmeCatalogo>();
    for (const c of itens) m.set(chave(c.fonte, c.error_code, c.error_code_info), c);
    return m;
}

export function buscarCatalogo(
    indice: Map<string, AlarmeCatalogo>,
    alarme: Pick<AlarmeGeradora, 'fonte' | 'error_code' | 'error_code_info'>,
): AlarmeCatalogo | null {
    const variante = alarme.error_code_info ?? CURINGA;
    return indice.get(chave(alarme.fonte, alarme.error_code, variante))
        ?? indice.get(chave(alarme.fonte, alarme.error_code, CURINGA))
        ?? null;
}

/**
 * Junta linhas que são a mesma falha, na mesma usina, quase no mesmo instante.
 *
 * A chave inclui a variante (`error_code_info`) de propósito: na FOMware um
 * `GridF.OutLim` é frequência alta E baixa, e juntar os dois num card só
 * esconderia dois problemas diferentes atrás de um rótulo.
 *
 * A janela conta a partir do PRIMEIRO alarme do grupo, não do anterior. Se
 * contasse do anterior, uma sequência de disparos de 10 em 10 minutos se
 * encadearia num grupo de horas.
 */
export function agruparAlarmes(
    alarmes: AlarmeGeradora[],
    janelaMs: number = JANELA_EVENTO_MS,
): EventoAlarme[] {
    const baldes = new Map<string, AlarmeGeradora[]>();
    for (const a of alarmes) {
        const k = chave(a.fonte, `${a.site_id_api}|${a.error_code}`, a.error_code_info ?? '');
        const lista = baldes.get(k);
        if (lista) lista.push(a); else baldes.set(k, [a]);
    }

    const eventos: EventoAlarme[] = [];
    for (const lista of baldes.values()) {
        const ordenada = [...lista].sort(
            (x, y) => Date.parse(x.triggered_at) - Date.parse(y.triggered_at),
        );
        let grupo: AlarmeGeradora[] = [];
        let abertura = 0;

        const fechar = () => { if (grupo.length) eventos.push(montarEvento(grupo)); };

        for (const a of ordenada) {
            const t = Date.parse(a.triggered_at);
            if (!grupo.length || t - abertura > janelaMs) {
                fechar();
                grupo = [a];
                abertura = t;
            } else {
                grupo.push(a);
            }
        }
        fechar();
    }

    return eventos.sort((x, y) => Date.parse(y.inicio) - Date.parse(x.inicio));
}

function montarEvento(grupo: AlarmeGeradora[]): EventoAlarme {
    const inversores = [...new Set(grupo.map(a => a.mb_device_sn || a.mb_device_code))];
    // Só está encerrado se TODOS acabaram. Um inversor ainda em falha mantém o
    // evento aberto — o contrário daria "resolvido" com equipamento parado.
    const encerrado = grupo.every(a => !!a.resolvido_em);
    const fins = grupo.map(a => a.resolvido_em).filter((s): s is string => !!s);
    return {
        id: grupo.reduce((min, a) => (a.id < min ? a.id : min), grupo[0].id),
        representante: grupo[0],
        linhas: grupo,
        inversores,
        disparos: grupo.reduce((s, a) => s + (a.ocorrencias ?? 1), 0),
        inicio: grupo[0].triggered_at,
        fim: encerrado && fins.length
            ? fins.reduce((max, s) => (Date.parse(s) > Date.parse(max) ? s : max), fins[0])
            : null,
        encerrado,
    };
}

/**
 * Título de um alarme como a tela mostra.
 *
 * Sem catálogo o rótulo cai no texto do fabricante — que na Canadian vem em
 * chinês. Aí o código (A-200, F-207) informa mais do que 电网电压超出范围.
 */
export function tituloAlarme(
    alarme: Pick<AlarmeGeradora, 'fonte' | 'error_code' | 'error_code_info'>,
    catalogo: AlarmeCatalogo | null | undefined,
): string {
    return catalogo?.titulo
        || (!catalogo && alarme.fonte === 'canadian' ? `Código ${alarme.error_code}` : null)
        || alarme.error_code_info
        || alarme.error_code;
}

/** Título do evento: o do alarme que o representa. */
export function tituloEvento(evento: EventoAlarme): string {
    return tituloAlarme(evento.representante, evento.representante.catalogo);
}

/** "5 min", "2 h 10 min", "3 dias" — duração legível de um evento fechado. */
export function duracaoEvento(evento: EventoAlarme): string | null {
    if (!evento.fim) return null;
    const ms = Date.parse(evento.fim) - Date.parse(evento.inicio);
    if (!Number.isFinite(ms) || ms < 0) return null;
    const min = Math.round(ms / 60000);
    if (min < 1) return 'menos de 1 min';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
    const d = Math.floor(h / 24);
    return d === 1 ? '1 dia' : `${d} dias`;
}
